var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useMemo, useState } from "react";
import { COLUMN_LAYOUT_STORAGE_KEY_PREFIX, COLUMN_MIN_WIDTH, } from "../utils/constants";
var EMPTY_LAYOUT = { order: [], widths: {} };
var storageKeyFor = function (layoutKey) {
    return "".concat(COLUMN_LAYOUT_STORAGE_KEY_PREFIX, ".").concat(layoutKey);
};
/**
 * Read and sanitise the stored layout for a list. Never throws: malformed JSON or unexpected
 * shapes fall back to an empty layout so the list always renders.
 *
 * @param layoutKey stable per-list key (see `COLUMN_LAYOUT_KEYS`)
 * @returns the parsed layout, or an empty layout when nothing valid is stored
 */
var readStoredLayout = function (layoutKey) {
    try {
        var raw = localStorage.getItem(storageKeyFor(layoutKey));
        if (!raw)
            return EMPTY_LAYOUT;
        var parsed = JSON.parse(raw);
        var order = Array.isArray(parsed.order)
            ? parsed.order.filter(function (id) { return typeof id === "string"; })
            : [];
        var widths = parsed.widths && typeof parsed.widths === "object" ? parsed.widths : {};
        return { order: order, widths: widths };
    }
    catch (_a) {
        return EMPTY_LAYOUT;
    }
};
/**
 * Manage a list's column order and widths, persisted per browser (localStorage) — no backend.
 * Modelled on `usePageSize`: the source of truth for defaults is the incoming `columns` array,
 * and only user-driven changes are stored. Stored ids are reconciled against the live columns on
 * every render, so columns added/removed in code (e.g. a permission-gated action column) are
 * handled gracefully.
 *
 * @param layoutKey stable per-list key (see `COLUMN_LAYOUT_KEYS`)
 * @param columns the list's default column definitions in their natural order
 * @returns reordered/resized columns plus `reorder`, `setWidth`, `reset` and `isCustomized`
 */
export function useColumnLayout(layoutKey, columns) {
    var _a = useState(function () {
        return readStoredLayout(layoutKey);
    }), layout = _a[0], setLayout = _a[1];
    var getColumnId = useCallback(function (column) { var _a; return (_a = column.id) !== null && _a !== void 0 ? _a : column.key; }, []);
    var persist = useCallback(function (next) {
        setLayout(next);
        localStorage.setItem(storageKeyFor(layoutKey), JSON.stringify(next));
    }, [layoutKey]);
    // Live column ids in their natural (code-defined) order.
    var baseIds = useMemo(function () { return columns.map(getColumnId); }, [columns, getColumnId]);
    // Effective display order: stored order (minus stale ids), with every column missing from it
    // (added in code after the user customised the layout) inserted right after its natural
    // predecessor — so a new column lands where the code defines it, not behind e.g. a trailing
    // actions column. A missing first column goes to the start, a missing last column (e.g. a
    // permission-gated actions column) to the end.
    var effectiveOrder = useMemo(function () {
        var known = new Set(baseIds);
        var ordered = layout.order.filter(function (id) { return known.has(id); });
        baseIds.forEach(function (id, index) {
            if (ordered.includes(id))
                return;
            if (index === baseIds.length - 1) {
                ordered.push(id);
                return;
            }
            var predecessor = index > 0 ? baseIds[index - 1] : undefined;
            var insertAt = predecessor === undefined ? 0 : ordered.indexOf(predecessor) + 1;
            ordered.splice(insertAt, 0, id);
        });
        return ordered;
    }, [baseIds, layout.order]);
    var orderedColumns = useMemo(function () {
        var byId = new Map(columns.map(function (column) { return [getColumnId(column), column]; }));
        return effectiveOrder
            .map(function (id) { return byId.get(id); })
            .filter(function (column) { return column !== undefined; })
            .map(function (column) {
            var width = layout.widths[getColumnId(column)];
            return width !== undefined ? __assign(__assign({}, column), { width: width }) : column;
        });
    }, [columns, effectiveOrder, getColumnId, layout.widths]);
    var reorder = useCallback(function (activeId, overId) {
        var from = effectiveOrder.indexOf(activeId);
        var to = effectiveOrder.indexOf(overId);
        if (from === -1 || to === -1 || from === to)
            return;
        var next = __spreadArray([], effectiveOrder, true);
        var moved = next.splice(from, 1)[0];
        next.splice(to, 0, moved);
        persist({ order: next, widths: layout.widths });
    }, [effectiveOrder, layout.widths, persist]);
    var setWidth = useCallback(function (id, width) {
        var _a;
        var clamped = Math.max(COLUMN_MIN_WIDTH, Math.round(width));
        persist({
            order: effectiveOrder,
            widths: __assign(__assign({}, layout.widths), (_a = {}, _a[id] = clamped, _a)),
        });
    }, [effectiveOrder, layout.widths, persist]);
    var reset = useCallback(function () {
        localStorage.removeItem(storageKeyFor(layoutKey));
        setLayout(EMPTY_LAYOUT);
    }, [layoutKey]);
    var isCustomized = layout.order.length > 0 || Object.keys(layout.widths).length > 0;
    return { columns: orderedColumns, reorder: reorder, setWidth: setWidth, reset: reset, isCustomized: isCustomized };
}
